import { Fragment } from "react";
import { ArrowUpRight, CalendarDays, CheckCircle2, Clock3 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { trackEvent } from "../../lib/analytics";
import styles from "./ProductionScheduleDemo.module.css";

type ScheduleItem = {
  label: string;
  meta: string;
  due: string;
  start: number;
  width: number;
  tone: "orange" | "blue" | "teal" | "violet";
  changeover?: string;
};

const lines: Array<{ name: string; detail: string; items: ScheduleItem[] }> = [
  {
    name: "Line 1 · 6–18 in",
    detail: "1,150 lb/hr",
    items: [
      {
        label: "HDPE Pipe · 8 in SDR 11",
        meta: "72,000 ft · SO-1842",
        due: "Due Thu",
        start: 4,
        width: 42,
        tone: "orange",
        changeover: "75m die + resin",
      },
      {
        label: "HDPE Pipe · 12 in SDR 17",
        meta: "48,000 ft · SO-1851",
        due: "Due Fri",
        start: 55,
        width: 39,
        tone: "blue",
      },
    ],
  },
  {
    name: "Line 2 · 2–6 in",
    detail: "86 ft/min",
    items: [
      {
        label: "PVC Conduit · 4 in",
        meta: "96,000 ft · SO-1838",
        due: "Due Wed",
        start: 10,
        width: 46,
        tone: "teal",
        changeover: "45m tooling",
      },
      {
        label: "Irrigation Tubing · 3 in",
        meta: "120 reels · SO-1849",
        due: "Due Fri",
        start: 65,
        width: 29,
        tone: "violet",
      },
    ],
  },
  {
    name: "Line 3 · Profile",
    detail: "18 ft/min",
    items: [
      {
        label: "Extruded Profile · Gray PVC",
        meta: "48,000 ft · SO-1844",
        due: "Due Thu",
        start: 4,
        width: 53,
        tone: "violet",
        changeover: "60m color + die",
      },
    ],
  },
  {
    name: "Line 4 · 1–4 in",
    detail: "74 ft/min",
    items: [
      {
        label: "PVC Conduit · 2 in",
        meta: "44,000 ft · SO-1853",
        due: "Due Fri",
        start: 24,
        width: 38,
        tone: "teal",
      },
      {
        label: "Irrigation Tubing · 3 in",
        meta: "80 reels · SO-1849",
        due: "Due Fri",
        start: 69,
        width: 25,
        tone: "orange",
      },
    ],
  },
];

export default function ProductionScheduleDemo() {
  const { t } = useTranslation();

  const trackPlayground = () => {
    trackEvent("tool_cta_clicked", {
      tool: "scheduling_playground",
      placement: "homepage_product_demo",
    });
  };

  return (
    <section className={styles.section} aria-labelledby="schedule-demo-heading">
      <div className={styles.intro}>
        <div>
          <p className={styles.eyebrow}>{t("pages.home.productDemo.label")}</p>
          <h2 id="schedule-demo-heading">{t("pages.home.productDemo.title")}</h2>
        </div>
        <div className={styles.introCopy}>
          <p>{t("pages.home.productDemo.body")}</p>
          <a href="https://demo.runmark.app" onClick={trackPlayground}>
            {t("pages.home.productDemo.cta")} <ArrowUpRight aria-hidden="true" />
          </a>
        </div>
      </div>

      <div className={styles.demoShell}>
        <div className={styles.demoTopbar}>
          <div>
            <span className={styles.windowDot} />
            <span className={styles.windowDot} />
            <span className={styles.windowDot} />
          </div>
          <strong>{t("pages.home.productDemo.scheduleName")}</strong>
          <span className={styles.simulated}>{t("pages.home.productDemo.simulated")}</span>
        </div>

        <div className={styles.demoToolbar}>
          <div>
            <CalendarDays aria-hidden="true" />
            <span>Oct 19–23, 2026</span>
          </div>
          <div className={styles.toolbarStatus}>
            <span><CheckCircle2 aria-hidden="true" /> 7 {t("pages.home.productDemo.runs")}</span>
            <span><Clock3 aria-hidden="true" /> 3 {t("pages.home.productDemo.changeovers")}</span>
          </div>
        </div>

        <div className={styles.boardScroll}>
          <div className={styles.board} role="img" aria-label={t("pages.home.productDemo.boardAria")}>
            <div className={styles.boardHeader}>
              <span>{t("pages.home.productDemo.line")}</span>
              {["Mon 19", "Tue 20", "Wed 21", "Thu 22", "Fri 23"].map((day) => (
                <span key={day}>{day}</span>
              ))}
            </div>
            {lines.map((line) => (
              <div className={styles.lineRow} key={line.name}>
                <div className={styles.lineLabel}>
                  <strong>{line.name}</strong>
                  <span>{line.detail}</span>
                </div>
                <div className={styles.track}>
                  {[1, 2, 3, 4].map((division) => <span key={division} />)}
                  {line.items.map((item) => (
                    <Fragment key={`${item.label}-${item.start}`}>
                      {item.changeover && (
                        <div
                          className={styles.changeoverBlock}
                          style={{ left: `${Math.max(0, item.start - 4)}%`, width: "4%" }}
                          title={item.changeover}
                        />
                      )}
                      <div
                        className={`${styles.run} ${styles[item.tone]}`}
                        style={{ left: `${item.start}%`, width: `${item.width}%` }}
                      >
                        <strong>{item.label}</strong>
                        <span>{item.meta}</span>
                        <b>{item.due}</b>
                      </div>
                    </Fragment>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.demoFooter}>
          <p>{t("pages.home.productDemo.disclaimer")}</p>
          <div>
            <span><i className={styles.orangeSwatch} /> HDPE</span>
            <span><i className={styles.tealSwatch} /> PVC / conduit</span>
            <span><i className={styles.violetSwatch} /> Profile / tubing</span>
            <span><i className={styles.changeoverSwatch} /> Changeover</span>
          </div>
        </div>
      </div>
    </section>
  );
}
